import * as signalR from '@microsoft/signalr';
import { configService } from '../../config/config-service';
import { getMsalAccessToken } from '../../auth/msal';

/**
 * Module-level SignalR connection (app lifetime).
 * Supports multiple subscribers (Header + Notifications page) without
 * stealing each other's ReceiveNotification handler.
 */
let connection = null;
let starting = null;
const subscribers = new Set();

function getHubUrl() {
  const base = (configService.notificationApiUrl || 'https://localhost:54191').replace(/\/$/, '');
  return `${base}/hubs/notifications`;
}

function dispatchToSubscribers(dto) {
  subscribers.forEach(handler => {
    try {
      handler(dto);
    } catch (err) {
      console.error('SignalR subscriber failed', err);
    }
  });
}

export function getNotificationHubConnection() {
  return connection;
}

/**
 * Subscribe to live notifications. Returns an unsubscribe function.
 * @param {(dto: object) => void} onReceiveNotification
 */
export function subscribeNotificationHub(onReceiveNotification) {
  if (typeof onReceiveNotification === 'function') {
    subscribers.add(onReceiveNotification);
  }
  return () => {
    if (typeof onReceiveNotification === 'function') {
      subscribers.delete(onReceiveNotification);
    }
  };
}

/**
 * Ensure hub is connected. Optionally register a subscriber.
 * @param {(dto: object) => void} [onReceiveNotification]
 */
export async function startNotificationHub(onReceiveNotification) {
  const unsubscribe =
    typeof onReceiveNotification === 'function'
      ? subscribeNotificationHub(onReceiveNotification)
      : () => {};

  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    return { connection, unsubscribe };
  }

  if (starting) {
    await starting;
    return { connection, unsubscribe };
  }

  connection = new signalR.HubConnectionBuilder()
    .withUrl(getHubUrl(), {
      accessTokenFactory: async () => (await getMsalAccessToken()) || '',
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  connection.on('ReceiveNotification', dto => {
    dispatchToSubscribers(dto);
  });

  starting = (async () => {
    try {
      await connection.start();
      console.log('SignalR connected to', getHubUrl());
      return connection;
    } catch (err) {
      console.error('SignalR start failed', err);
      connection = null;
      throw err;
    } finally {
      starting = null;
    }
  })();

  await starting;
  return { connection, unsubscribe };
}

/** Call on logout / full app teardown only — not on Header/page unmount. */
export async function stopNotificationHub() {
  subscribers.clear();
  if (starting) {
    try {
      await starting;
    } catch {
      // ignore failed start
    }
  }
  if (!connection) return;
  try {
    await connection.stop();
  } catch (err) {
    console.error('SignalR stop failed', err);
  } finally {
    connection = null;
    starting = null;
  }
}
